/**
 * Kalendertage in der Anzeigezone -- F1-53, F1-54.
 *
 * **Gerechnet wird in `Europe/Berlin` und nicht in der Zone des Browsers.**
 * Der Server liefert UTC (docs/02, Abschnitt 1), angezeigt wird in
 * `DISPLAY_TIME_ZONE`. Ein Kalender, der seine Tagesgrenzen aus dem Browser
 * naehme, zoege sie bei einem Kollegen auf Dienstreise in Lissabon eine
 * Stunde anders als die Uhrzeiten, die er anzeigt -- und der Termin um
 * 23:30 Uhr stuende am falschen Tag.
 *
 * Ein Tag ist hier eine Zeichenkette `JJJJ-MM-TT`: Sie hat keine Zone und
 * damit auch keine, die sich unbemerkt einmischt.
 */

import { DISPLAY_TIME_ZONE } from '../../i18n';

export type CalendarView = 'week' | 'month';

const WALL_CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

interface WallTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Was eine Uhr in Berlin zu diesem Zeitpunkt zeigt. */
function wallTimeOf(instant: Date): WallTime {
  const parts = Object.fromEntries(
    WALL_CLOCK.formatToParts(instant).map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
}

function offsetMinutes(instant: Date): number {
  const wall = wallTimeOf(instant);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  return Math.round((asUtc - Math.floor(instant.getTime() / 60_000) * 60_000) / 60_000);
}

/**
 * Eine Berliner Uhrzeit als Zeitpunkt.
 *
 * Zweimal gerechnet, weil der Versatz vom Ergebnis abhaengt: An den Tagen
 * der Zeitumstellung liegt die geratene Stelle auf der anderen Seite der
 * Umstellung als die richtige.
 */
export function berlinToInstant(day: string, time = '00:00'): Date {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const [hour, minute] = time.split(':').map(Number) as [number, number];
  const guess = Date.UTC(year, month - 1, date, hour, minute);
  const first = guess - offsetMinutes(new Date(guess)) * 60_000;
  const second = guess - offsetMinutes(new Date(first)) * 60_000;
  return new Date(second);
}

/** Der Kalendertag eines Zeitpunkts in Berlin. */
export function dayOf(instant: Date | string): string {
  const wall = wallTimeOf(typeof instant === 'string' ? new Date(instant) : instant);
  return `${String(wall.year)}-${pad(wall.month)}-${pad(wall.day)}`;
}

/** Die Berliner Uhrzeit eines Zeitpunkts als `HH:MM`. */
export function timeOf(instant: Date | string): string {
  const wall = wallTimeOf(typeof instant === 'string' ? new Date(instant) : instant);
  return `${pad(wall.hour)}:${pad(wall.minute)}`;
}

export function today(): string {
  return dayOf(new Date());
}

/** Ein Tag weiter oder zurueck -- ohne Zone, also ohne Umstellungsfehler. */
export function addDays(day: string, amount: number): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, date + amount));
  return shifted.toISOString().slice(0, 10);
}

/** Wochentag, Montag = 0. */
function weekdayOf(day: string): number {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(year, month - 1, date)).getUTCDay() + 6) % 7;
}

export function startOfWeek(day: string): string {
  return addDays(day, -weekdayOf(day));
}

export function startOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function addMonths(day: string, amount: number): string {
  const [year, month] = day.split('-').map(Number) as [number, number];
  const shifted = new Date(Date.UTC(year, month - 1 + amount, 1));
  return shifted.toISOString().slice(0, 10);
}

/**
 * Die Tage, die eine Ansicht zeigt.
 *
 * Die Woche beginnt am Montag. Der Monat wird auf ganze Wochen aufgefuellt
 * -- vom Montag vor dem Ersten bis zum Sonntag nach dem Letzten --, damit das
 * Raster sieben Spalten behaelt und ein Termin am 31. nicht in einer
 * halbleeren Zeile steht, deren Wochentage niemand mehr zuordnen kann.
 */
export function daysOfView(view: CalendarView, anchor: string): string[] {
  if (view === 'week') {
    const first = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, index) => addDays(first, index));
  }

  const first = startOfWeek(startOfMonth(anchor));
  const nextMonth = addMonths(anchor, 1);
  const days: string[] = [];
  for (let day = first; day < nextMonth || weekdayOf(day) !== 0; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

/** Der Zeitraum eines oder mehrerer Tage als Grenzen fuer den Server. */
export function rangeOf(first: string, lastInclusive: string): { since: string; until: string } {
  return {
    since: berlinToInstant(first).toISOString(),
    until: berlinToInstant(addDays(lastInclusive, 1)).toISOString(),
  };
}

/**
 * Ob ein Termin einen Tag beruehrt.
 *
 * Nach Ueberschneidung und nicht nach Beginn -- dieselbe Frage, die der
 * Server stellt: Ein Termin von 23 bis 1 Uhr steht an beiden Tagen.
 */
export function touchesDay(startsAt: string, endsAt: string, day: string): boolean {
  const { since, until } = rangeOf(day, day);
  return new Date(endsAt) > new Date(since) && new Date(startsAt) < new Date(until);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
